/**
 * Enterprise Systems List Component
 *
 * Displays a grid of enterprise system cards with:
 * - Loading states
 * - Empty states
 * - Error handling
 * - Grid/List view toggle
 * - Responsive layout
 */

import React, { useState } from 'react';
import { Grid3x3, List, AlertCircle, Loader2 } from 'lucide-react';
import EnterpriseSystemCard from './EnterpriseSystemCard';
import type { EnterpriseSystem } from '../../lib/api/enterpriseSystemsApi';

interface EnterpriseSystemsListProps {
  systems: EnterpriseSystem[];
  loading?: boolean;
  error?: Error | null;
  onSystemClick?: (system: EnterpriseSystem) => void;
  emptyMessage?: string;
}

type ViewMode = 'grid' | 'list';

const EnterpriseSystemsList: React.FC<EnterpriseSystemsListProps> = ({
  systems,
  loading = false,
  error = null,
  onSystemClick,
  emptyMessage = 'No enterprise systems found.',
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // Loading State
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
          <p className="text-neutral-600">Loading enterprise systems...</p>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            Failed to Load Systems
          </h3>
          <p className="text-neutral-600 text-sm">
            {error.message || 'An error occurred while loading enterprise systems.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-6 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Empty State
  if (!systems || systems.length === 0) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Grid3x3 className="w-8 h-8 text-neutral-400" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            No Systems Found
          </h3>
          <p className="text-neutral-600 text-sm">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-600">
          Showing <span className="font-semibold">{systems.length}</span>{' '}
          {systems.length === 1 ? 'system' : 'systems'}
        </p>

        <div className="flex items-center gap-2 bg-neutral-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'grid'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
            aria-label="Grid view"
          >
            <Grid3x3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-md transition-colors ${
              viewMode === 'list'
                ? 'bg-white text-primary-600 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
            aria-label="List view"
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Systems Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {systems.map((system) => (
            <EnterpriseSystemCard
              key={system.system_id}
              system={system}
              onClick={onSystemClick}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {systems.map((system) => (
            <EnterpriseSystemCard
              key={system.system_id}
              system={system}
              onClick={onSystemClick}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default EnterpriseSystemsList;
